/**
 * ConversationMiniRail — the ONE collapsible left sidebar (Jul 2026).
 *
 * A single bar, ``[icon][label]`` rows throughout. The host in ``route.tsx``
 * clips it to 56px (collapsed → only the icons show) or 384px (expanded → the
 * labels reveal at the SAME level as their icon, and the conversation list
 * appears below). No separate rail + panel, no duplicates — the icon never
 * moves, only the text opens. This is the Claude/shadcn collapsible pattern:
 * the content is a fixed 384px column, the parent animates ``width`` in CSS.
 *
 * Rows, top → bottom:
 *   1. Ando logo + project switcher
 *   2. Expand/collapse · New chat · Search
 *   3. Conversation list (embedded ``ConversationSidebar``, expanded only)
 *   4. Spazi — Canvas / Analisi / Automazioni / Flows / Connect
 *   5. Notifications · Studio · Account
 */

import { AndoLogo, Tooltip, TooltipContent, TooltipTrigger, cn } from "@ando/ui";
import { AndoWordmark } from "@ando/ui/wordmark";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { PanelLeftClose, PanelLeftOpen, Plus, Search, Wand2 } from "lucide-react";
import { type FC, Fragment, type ReactNode } from "react";

import { UserMenuButton } from "@/features/account/components/UserMenuButton";
import {
  selectActiveTab,
  selectSetTab,
  useWorkspaceStore,
} from "@/features/workspace-router/state/workspaceStore";
import { useUnseenSharesCount } from "@/features/share/hooks/useUnseenSharesCount";
import { NotificationBell } from "@/features/workspace/components/NotificationPill";
import { SPAZI_TAB_DEFS } from "@/features/workspace/config";
import { ConversationSidebar } from "./ConversationSidebar";

export interface ConversationMiniRailProps {
  onExpand: () => void;
  onOpenPalette: () => void;
  onNewChat: () => void;
  /** Row-select handler forwarded to the embedded conversation list. */
  onSelect?: (id: string) => void;
  /** Dismiss handler forwarded to the embedded conversation list. */
  onClose?: () => void;
  isExpanded?: boolean;
}

/**
 * NavRow — one ``[icon][label]`` row. The icon sits at a FIXED left inset
 * (``pl-[18px]``) so it never moves between the collapsed (56px, label clipped)
 * and expanded (384px, label visible) states. A tooltip surfaces the label
 * while collapsed.
 */
const NavRow: FC<{
  icon: ReactNode;
  label: string;
  onClick: () => void;
  expanded: boolean;
  active?: boolean;
  accent?: boolean;
  testId?: string;
  /** Decorative node placed right AFTER the label, revealed only when expanded
   *  (the collapsed 56px bar has no room). Used for the new-chat blob. */
  trailing?: ReactNode;
  /** Unread-style count pinned to the icon cell (both states). 0 = hidden. */
  badge?: number;
}> = ({ icon, label, onClick, expanded, active, accent, testId, trailing, badge }) => {
  const btn = (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      data-testid={testId}
      className={cn(
        // ONE height across both states (40px). It was 44 collapsed / 36
        // expanded, and animating that difference only smeared the jump
        // instead of removing it: in a panel that slides open sideways the
        // eye expects the WIDTH to be the only thing moving. 40 keeps a
        // comfortable icon target while still saving ~36px over the nine
        // rows compared to the old 44.
        "group relative flex h-10 shrink-0 items-center transition-colors",
        // Collapsed → the button is EXACTLY the 56px icon cell so the hover
        // tooltip (``side="right"``) anchors right BESIDE the icon. With
        // ``w-full`` the button stayed 384px (the parent only clips it), so the
        // tooltip popped far out where the expanded label would be. The label /
        // blob overflow past 56px and are clipped by the parent — visually
        // identical, but the trigger box now matches what's on screen.
        // 36px expanded / 44px collapsed. Collapsed, the row IS the touch
        // target for a label-less icon, so it keeps the comfortable size.
        // Expanded, the label already says what it is: nine rows at 44px ate
        // ~400px before the project list even started. 36px also matches the
        // conversation rows, so the open sidebar reads on one rhythm.
        expanded ? "w-full pr-3" : "w-14",
        // The wash is painted by an inset pseudo-element, NOT by the button's
        // own background: the box keeps the geometry above (so the glyph never
        // shifts between collapsed and expanded, and the label still starts at
        // the clip edge) while the highlight stops running edge to edge like a
        // stripe across the rail. Same 6px inset in both states — one rhythm.
        "before:absolute before:inset-y-0 before:right-1.5 before:left-1.5 before:rounded-md before:transition-colors before:content-['']",
        // Neutral wash, not orange: the sidebar is chrome. With every row
        // washing orange the accent stopped meaning anything — it has to
        // stay rare enough to still say "Ando" / "act here".
        "hover:before:bg-ink-1/[0.05]",
        // Ring on the pseudo-element too, or focus would outline the full
        // 56px box while the visible control is the inset 44px pill.
        "focus-visible:outline-none focus-visible:before:ring-2 focus-visible:before:ring-ando-orange/45",
        // Accent survives in exactly two spots: the "+" (the primary
        // action) and the ACTIVE glyph while COLLAPSED, where there is no
        // label to carry the state. Expanded, the type ladder carries it.
        accent
          ? "text-ando-orange"
          : active
            ? expanded
              ? "font-semibold text-ink-1"
              : "text-ando-orange"
            : "text-ink-2 hover:text-ink-1",
      )}
    >
      {/* Icon lives in a fixed 56px cell = the collapsed width, so the icon is
          centred while collapsed and the label starts exactly at the clip edge
          → fully hidden until the bar widens. ``relative`` on the content: a
          positioned ::before paints above static siblings, so without it the
          wash would cover the glyph and the label. */}
      <span className="relative flex h-10 w-14 shrink-0 items-center justify-center">
        {icon}
        {badge ? (
          // Orange is a signal here, not chrome: "something new for you".
          <span className="absolute top-1.5 right-2.5 grid h-4 min-w-4 place-items-center rounded-full bg-ando-orange px-1 font-semibold text-3xs text-white tabular-nums leading-none">
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </span>
      <span className="relative truncate whitespace-nowrap font-medium text-[13px]">{label}</span>
      {expanded && trailing ? (
        <span className="relative flex shrink-0 items-center pl-2.5">{trailing}</span>
      ) : null}
    </button>
  );
  if (expanded) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export const ConversationMiniRail: FC<ConversationMiniRailProps> = ({
  onExpand,
  onOpenPalette,
  onNewChat,
  onSelect,
  onClose,
  isExpanded = false,
}) => {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const spaceActiveTab = useWorkspaceStore(selectActiveTab);
  const unseenShares = useUnseenSharesCount();
  const setSpaceTab = useWorkspaceStore(selectSetTab);

  return (
    <nav aria-label={t`Barra conversazioni`} className="flex h-full min-h-0 w-96 flex-col bg-cream">
      {/* ── Brand — collapsed shows the classic elephant (kept in the left
          56px so it stays visible); expanded swaps in the full animated
          wordmark (elephant + "Ando" with the drawing-"o" from the landing).
          Mounting on expand replays the o-animation. ─────────────────────── */}
      {/* Optical, not geometric, centring. The glyph is 33.9px wide (viewBox
          490.1×440.3 at 30.5px tall), so dead centre in the 56px rail is
          11px — which is what this used to be, and it read LEFT of centre.
          The mark is mirrored and its mass is asymmetric: solid body on one
          side, thin trunk rising on the other, so its visual centre sits
          left of its box. +3px puts the WEIGHT in the middle, which is what
          the eye actually measures — settled by eye, not by arithmetic. */}
      <div className="flex h-14 shrink-0 items-center pl-[14px]">
        {isExpanded ? (
          <AndoWordmark fontSize={24} glow={false} interactive={false} />
        ) : (
          <AndoLogo size={30} />
        )}
      </div>

      {/* ── Primary actions — New chat (the morphing orange blob) up top. ── */}
      <div className="flex shrink-0 flex-col gap-0.5 pt-1">
        <NavRow
          // The affordance is now an unambiguous "+" (SOTA new-chat convention:
          // Claude / ChatGPT / Linear). The morphing pencil-line blob — which on
          // its own didn't read as "add chat" — is demoted to a decorative accent
          // pinned to the RIGHT edge, visible only when the bar is expanded.
          icon={<Plus className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden />}
          label={t`Nuova chat`}
          onClick={onNewChat}
          expanded={isExpanded}
          accent
          testId="chat-rail-new"
          trailing={
            <span
              aria-hidden
              className={cn(
                "block h-4 w-4 border-[1.7px] border-current",
                "animate-blob-morph [animation-play-state:paused] motion-reduce:animate-none motion-reduce:rounded-[42%]",
                "transition-[box-shadow] duration-300",
                "group-hover:shadow-[0_0_12px_rgba(255,122,26,0.5)] group-hover:[animation-play-state:running]",
              )}
            />
          }
        />
        <NavRow
          icon={
            isExpanded ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )
          }
          label={isExpanded ? t`Comprimi la sidebar` : t`Espandi la sidebar`}
          onClick={onExpand}
          expanded={isExpanded}
          testId="chat-rail-expand"
        />
        <NavRow
          icon={<Search className="h-4 w-4" />}
          label={t`Cerca`}
          onClick={onOpenPalette}
          expanded={isExpanded}
          testId="chat-rail-search"
        />
      </div>

      {/* ── Spazi — grouped right under the primary actions (top), so the
          collapsed bar has no big void in the middle (à la Claude). The
          ``data-tour`` anchor lets the demo storyboard spotlight the block
          (Canvas / Analisi / Automazioni / Flows / Connect) after the top-bar
          tabs — these areas moved here in the Jul 2026 IA slim-down. ────── */}
      <div className="shrink-0" data-tour="spazi">
        <div className="mx-3 my-1 h-px bg-hairline" />
        {SPAZI_TAB_DEFS.map((def, i) => {
          const Icon = def.icon;
          return (
            <Fragment key={def.id}>
              <NavRow
                icon={<Icon className="h-4 w-4" />}
                label={i18n._(def.labelMsg)}
                active={spaceActiveTab === def.id}
                expanded={isExpanded}
                badge={def.id === "shares" ? unseenShares : undefined}
                onClick={() => {
                  setSpaceTab(def.id, "manual");
                  navigate({ to: def.route });
                  onClose?.();
                }}
              />
              {/* Ando Modes — slotted right after Canvas. Opens the Modes
                  settings surface (relocated off the removed Studio pill). */}
              {i === 0 ? (
                <NavRow
                  icon={<Wand2 className="h-4 w-4" />}
                  label={t`Ando Modes`}
                  expanded={isExpanded}
                  onClick={() => {
                    navigate({ to: "/settings/modes" });
                    onClose?.();
                  }}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>

      {/* ── Conversation list (expanded) fills the rest; collapsed it is just
          empty space that pushes the utility cluster to the foot. ──────── */}
      {isExpanded ? (
        // The list is MOUNTED on expand, so it needs an enter animation, not a
        // transition: a freshly mounted node has no previous opacity to travel
        // from. Same 200ms as the panel's width, so the rows arrive with the
        // panel instead of landing on it.
        <div className="motion-safe:fade-in min-h-0 flex-1 overflow-hidden pt-1 motion-safe:animate-in motion-safe:duration-200">
          <ConversationSidebar embedded onSelect={onSelect} onClose={onClose} />
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* ── Utility cluster — notifications · studio · account. Each icon is
          centred in the 56px cell so the column reads clean while collapsed. */}
      <div className="flex shrink-0 flex-col border-hairline border-t py-1">
        {/* Icon in the 56px cell + a label past the clip edge → the labels
            reveal beside the icons exactly like every other row. */}
        <div className="flex h-11 items-center">
          <span className="flex w-14 shrink-0 items-center justify-center">
            <NotificationBell />
          </span>
          <span className="truncate whitespace-nowrap font-medium text-[13px] text-ink-2">
            {t`Notifiche`}
          </span>
        </div>
        <div className="flex h-11 items-center">
          <span className="flex w-14 shrink-0 items-center justify-center">
            <UserMenuButton />
          </span>
          <span className="truncate whitespace-nowrap font-medium text-[13px] text-ink-2">
            {t`Account`}
          </span>
        </div>
      </div>
    </nav>
  );
};

ConversationMiniRail.displayName = "ConversationMiniRail";
